import * as React from 'react';
import { useState } from 'react';
import { lighten, useTheme } from '@mui/material/styles';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { capitalize } from 'lodash';
import PropTypes from 'prop-types';

const CustomizedAccordions = ({ records, onComparePatient }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(records[0]?.patient.mpi ?? false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const setComparePatient = (patient) => {
    onComparePatient(patient);
  };

  return (
    <Grid
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: 'background.paper',
        boxShadow: 3,
        borderRadius: 2,
        overflowY: 'auto',
        p: 1,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant='h6' fontWeight='bold' sx={{ px: 1, pb: 1, fontWeight: 600 }}>
        {`Duplicate matches for ${capitalize(records[0].patient.lastName)}, ${capitalize(
          records[0].patient.firstName
        )}`}
      </Typography>
      <Divider sx={{ mb: 1 }} />

      {records.map(({ patient }, index) => {
        const panelId = patient.mpi;
        return (
          <Accordion
            key={panelId}
            expanded={expanded === panelId}
            onChange={handleChange(panelId)}
            disableGutters
            square
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: lighten(theme.palette.primary.main, 0.9),
                px: 2,
                py: 1,
                minHeight: '48px',
              }}
            >
              <Typography fontWeight='medium'>Patient #{index + 1}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container direction='row' justifyContent='space-between' alignItems='center'>
                <Grid xs={10}>
                  <Typography fontWeight={500}>
                    {capitalize(patient.lastName)}, {capitalize(patient.firstName)}
                  </Typography>
                  <Typography fontWeight={500}>DOB: {patient.dob}</Typography>
                  <Typography fontWeight={500}>Gender: {patient.gender}</Typography>
                  <Typography fontWeight={500}>
                    Created at: {new Date(patient.createdTime).toLocaleString()}
                  </Typography>
                </Grid>

                <Grid display='flex' justifyContent='flex-end' xs={15}>
                  <Button
                    variant='contained'
                    color='primary'
                    size='small'
                    sx={{ mt: '10px' }}
                    onClick={() =>
                      setComparePatient({ ...patient, patientIdentifier: `#${index + 1}` })
                    }
                  >
                    Compare
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Grid>
  );
};

CustomizedAccordions.propTypes = {
  records: PropTypes.arrayOf({
    partner: PropTypes.shape({
      partnerName: PropTypes.string,
    }),
    patient: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      createdTime: PropTypes.string.isRequired,
      dob: PropTypes.string.isRequired,
      gender: PropTypes.string.isRequired,
    }),
    externalId: PropTypes.string,
    patientId: PropTypes.string.isRequired,
    practice: PropTypes.arrayOf(
      PropTypes.shape({
        practiceName: PropTypes.string,
      })
    ),
  }).isRequired,
  onComparePatient: PropTypes.func.isRequired,
};

export default CustomizedAccordions;
